import React, { ReactElement, forwardRef, useRef, useEffect, useCallback, useImperativeHandle, useState } from 'react';
import { useOffsetWidth } from './useOffsetWidth';
import { useScrollWidth } from './useScrollWidth';
import { useVisibleItemCount } from './useVisibleItemCount';
import { useGap } from './useGap';

const velocityThreshold = 0.75;
const relativeDistanceThreshold = 0.33;

export interface FixedSliderRef {
    maxPage: number;
    goToPage(page: number): void;
    goToPrevPage(): void;
    goToNextPage(): void;
}

export interface FixedSliderProps {
    children: ReactElement[];
    onMaxPageChange?(maxPage: number): unknown;
    onPageChange?(maxPage: number): unknown;
}

export const FixedSlider = forwardRef<FixedSliderRef, FixedSliderProps>(function FixedSlider(
    { children, onMaxPageChange, onPageChange }, ref): ReactElement {
    const sliderRef = useRef<HTMLDivElement | null>(null);
    const tapeRef = useRef<HTMLDivElement | null>(null);

    const sliderWidth = useOffsetWidth(sliderRef);
    const tapeWidth = useScrollWidth(tapeRef);
    const count = useVisibleItemCount(sliderRef);
    const gap = useGap(tapeRef);
    const maxPage = tapeWidth === 0 || sliderWidth === 0 ? 0 : Math.ceil((tapeWidth - (children.length - 1) * gap) / sliderWidth) - 1;
    const minOffset = -tapeWidth + sliderWidth;

    const pageRef = useRef(0);
    const offsetRef = useRef(0);
    const tempOffsetRef = useRef(0);
    const animationRef = useRef<Animation | null>(null);

    const [startIndex, setStartIndex] = useState(0);

    useEffect(() => {
        if (onMaxPageChange) {
            onMaxPageChange(maxPage);
        }
    }, [onMaxPageChange, maxPage]);

    const goToPage = useCallback((page: number) => {
        const { current: tapeEl } = tapeRef;
        if (tapeEl === null) {
            return;
        }

        page = Math.max(0, Math.min(page, maxPage));
        const offset = Math.min(Math.max(minOffset, -page * (sliderWidth + gap)), 0);

        const keyframes = [
            { transform: `translateX(${tempOffsetRef.current}px)` },
            { transform: `translateX(${offset}px)` },
        ];

        if (onPageChange) {
            onPageChange(page);
        }

        pageRef.current = page;
        offsetRef.current = offset;
        tempOffsetRef.current = offset;
        tapeEl.style.transform = `translateX(${offset}px)`;

        animationRef.current?.cancel();
        animationRef.current = tapeEl.animate(keyframes, { duration: 200, iterations: 1, easing: 'ease-out' });

        setStartIndex(page * count);
    }, [sliderWidth, gap, minOffset, maxPage, onPageChange]);

    const goToPrevPage = useCallback(() => {
        goToPage(pageRef.current - 1);
    }, [goToPage]);

    const goToNextPage = useCallback(() => {
        goToPage(pageRef.current + 1);
    }, [goToPage]);

    useImperativeHandle(
        ref,
        () => ({ maxPage, goToPage, goToPrevPage, goToNextPage }),
        [maxPage, goToPage, goToPrevPage, goToNextPage],
    );

    useEffect(() => {
        let mouseY = 0;
        const onMouseMove = (event: MouseEvent) => {
            mouseY = event.clientY;
        };
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }
            const { current: sliderEl } = sliderRef;
            if (sliderEl === null) {
                return;
            }
            const sliderRect = sliderEl.getBoundingClientRect();
            if (mouseY < sliderRect.y || mouseY > sliderRect.y + sliderRect.height) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            if (event.key === 'ArrowLeft') {
                goToPrevPage();
            } else {
                goToNextPage();
            }
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('keydown', onKeyDown);
        return (): void => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [goToPrevPage, goToNextPage]);

    useEffect(() => {
        const onResize = (): void => {
            goToPage(pageRef.current);
        };
        window.addEventListener('resize', onResize);
        return (): void => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        const { current: sliderEl } = sliderRef;
        if (sliderEl === null) {
            return;
        }

        const { current: tapeEl } = tapeRef;
        if (tapeEl === null) {
            return;
        }

        let isMoved = false;
        let startX = 0;
        let startY = 0;
        let prevX = 0;
        let prevT = 0;
        let velocity = 0;

        // shared handlers

        const setTemporaryOffset = (draggingDist: number): void => {
            let offset = offsetRef.current + draggingDist;
            if (offset > 0) {
                offset *= 0.33;
            } else if (offset < minOffset) {
                offset = minOffset - (minOffset - offset) * 0.33;
            }
            tempOffsetRef.current = offset;
            tapeEl.style.transform = `translateX(${offset}px)`;
        };

        const onStart = (x: number, y: number): void => {
            isMoved = false;
            startX = x;
            startY = y;
            prevX = x;
            prevT = Date.now();
        };

        const onMove = (event: Event, x: number): void => {
            event.preventDefault();
            event.stopPropagation();
            setTemporaryOffset(x - startX);
            const t = Date.now();
            velocity = (x - prevX) / (t - prevT);
            prevX = x;
            prevT = t;
        };

        const onEnd = (event: Event): void => {
            if (prevX === startX) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const dist = prevX - startX;
            const absDist = Math.abs(dist);
            const dir = absDist / dist;
            const relDist = absDist / sliderWidth;

            if (dir === -1 && (velocity <= -velocityThreshold || relDist > relativeDistanceThreshold)) {
                return goToNextPage();
            }
            if (dir === 1 && (velocity >= velocityThreshold || relDist > relativeDistanceThreshold)) {
                return goToPrevPage();
            }
            goToPage(pageRef.current);
        };

        // touch handlers

        const onTouchMove = (event: TouchEvent): void => {
            const x = event.touches[0].pageX;
            const y = event.touches[0].pageY;
            if (!isMoved) {
                if (Math.abs(x - startX) > Math.abs(y - startY)) {
                    isMoved = true;
                } else {
                    window.removeEventListener('touchmove', onTouchMove);
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    window.removeEventListener('touchend', onTouchEnd);
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    window.removeEventListener('touchcancel', onTouchEnd);
                    return;
                }
            }
            onMove(event, x);
        };

        const onTouchEnd = (event: TouchEvent): void => {
            window.removeEventListener('touchmove', onTouchMove);
            onEnd(event);
        };

        const onTouchStart = (event: TouchEvent): void => {
            const x = event.touches[0].pageX;
            const y = event.touches[0].pageY;
            onStart(x, y);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd, { once: true });
            window.addEventListener('touchcancel', onTouchEnd, { once: true });
        };

        // mouse handlers

        const onMouseMove = (event: MouseEvent): void => {
            const x = event.pageX;
            if (x !== startX) {
                tapeEl.style.pointerEvents = 'none';
            }
            onMove(event, x);
        };

        const onMouseUp = (event: MouseEvent): void => {
            window.removeEventListener('mousemove', onMouseMove);
            tapeEl.style.removeProperty('pointer-events');
            onEnd(event);
        };

        const onMouseDown = (event: MouseEvent): void => {
            event.stopPropagation();
            event.preventDefault();
            const x = event.pageX;
            const y = event.pageY;
            onStart(x, y);
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp, { once: true });
        };

        // attach/detach

        sliderEl.addEventListener('touchstart', onTouchStart);
        sliderEl.addEventListener('mousedown', onMouseDown);

        return (): void => {
            sliderEl.removeEventListener('touchstart', onTouchStart);
            sliderEl.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
            window.removeEventListener('touchcancel', onTouchEnd);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [sliderWidth, minOffset, goToNextPage, goToPrevPage, goToPage]);

    return (
        <div className='Slider' ref={sliderRef}>
            <div className='Slider-tape' ref={tapeRef}>
                {children.map((child, i) => {
                    const classNames = ['Slider-item'];
                    if (i < startIndex) {
                        classNames.push('Slider-item-hidden');
                        classNames.push('Slider-item-left');
                        classNames.push(`Slider-item-${startIndex - i}`);
                    }
                    if (i > startIndex + count - 1) {
                        classNames.push('Slider-item-hidden');
                        classNames.push('Slider-item-right');
                        classNames.push(`Slider-item-${i - (startIndex + count - 1)}`);
                    }
                    return <div key={i} className={classNames.join(' ')}>{child}</div>;
                })}
            </div>
        </div>
    );
});
